import React, { FC } from 'react';
import { useApolloClient, useMutation } from '@apollo/react-hooks';
import { displaySnackBar } from '../../../common/displaySnackbar';
import { ImportEntitiesInput } from '../../../types/graphql-global-types';
import CSVImportation, {
  IRawCSV,
} from '../../../common/components/CSVImportation/CSVImportation';
import { IMPORT_ENTITIES } from '../../../graphql/entity/mutation';
import {
  ImportEntities,
  ImportEntitiesVariables,
} from '../../../graphql/entity/types/ImportEntities';
import { getEnseigneType, getPhysicalModel } from '../../../utils/enseigne';

export interface ICSVEntities {
  ENSEIGNE_ID: string;
  ENSEIGNE_NOM: string;
  ENSEIGNE_TYPE: string;
  ENSEIGNE_MODELE: string;
  AGENCE_NOM: string;
  AGENCE_SIRET: string;
  AGENCE_ADRESSE: string;
  AGENCE_CP: string;
  AGENCE_VILLE: string;
  AGENCE_TELEPHONE: string;
  AGENCE_EMAIL: string;
}

const HEADERS = [
  'ENSEIGNE_ID',
  'ENSEIGNE_NOM',
  'ENSEIGNE_TYPE',
  'ENSEIGNE_MODELE',
  'AGENCE_NOM',
  'AGENCE_SIRET',
  'AGENCE_ADRESSE',
  'AGENCE_CP',
  'AGENCE_VILLE',
  'AGENCE_TELEPHONE',
  'AGENCE_EMAIL',
];

const samples = [
  {
    ENSEIGNE_ID: '123',
    ENSEIGNE_NOM: '<Enseigne existante>',
    ENSEIGNE_TYPE:
      '<CONNECTCREDIT | TRANSACTION IMMOBILIERE | NOTARIAL | BANQUE | PROMOTION IMMOBILIERE | GESTION PATRIMONIALE | ASSURANCE | PARRAINAGE>',
    ENSEIGNE_MODELE: '<AVEC AGENCE | SANS AGENCE>',
    AGENCE_NOM: '<Agence à créer>',
    AGENCE_SIRET: '',
    AGENCE_ADRESSE: '',
    AGENCE_CP: '',
    AGENCE_VILLE: '',
    AGENCE_TELEPHONE: '',
    AGENCE_EMAIL: '',
  },
  {
    ENSEIGNE_ID: '_1',
    ENSEIGNE_NOM: '<Enseigne à créer>',
    ENSEIGNE_TYPE: '',
    ENSEIGNE_MODELE: '<AVEC AGENCE | SANS AGENCE>',
    AGENCE_NOM: '<Agence à créer>',
    AGENCE_SIRET: '',
    AGENCE_ADRESSE: '',
    AGENCE_CP: '',
    AGENCE_VILLE: '',
    AGENCE_TELEPHONE: '',
    AGENCE_EMAIL: '',
  },
  {
    ENSEIGNE_ID: '_1',
    ENSEIGNE_NOM: '<Enseigne déjà créée>',
    ENSEIGNE_TYPE: '',
    ENSEIGNE_MODELE: '<AVEC AGENCE | SANS AGENCE>',
    AGENCE_NOM: '<Agence à créer>',
    AGENCE_SIRET: '',
    AGENCE_ADRESSE: '',
    AGENCE_CP: '',
    AGENCE_VILLE: '',
    AGENCE_TELEPHONE: '',
    AGENCE_EMAIL: '',
  },
];

const reformatData = (
  rawData: IRawCSV<ICSVEntities>[],
): ImportEntitiesInput[] => {
  return rawData.map(({ data }) => ({
    enseigneId: data['ENSEIGNE_ID'],
    enseigneName: data['ENSEIGNE_NOM'],
    enseigneType: getEnseigneType(data['ENSEIGNE_TYPE']),
    enseignePhysicalModel: getPhysicalModel(data['ENSEIGNE_MODELE']),

    agencyName: data['AGENCE_NOM'],
    agencySiret: data['AGENCE_SIRET'],
    agencyPhone: data['AGENCE_TELEPHONE'],
    agencyEmail: data['AGENCE_EMAIL'],
    agencyAddress: data['AGENCE_ADRESSE'],
    agencyZipCode: data['AGENCE_CP'],
    agencyTown: data['AGENCE_VILLE'],
  }));
};

interface IEntitiesCSVImportationModal {
  handleClose: () => void;
}

const EntitiesCSVImportationModal: FC<IEntitiesCSVImportationModal> = (
  props,
) => {
  const { handleClose } = props;
  const client = useApolloClient();

  const [importEntities, { loading }] = useMutation<
    ImportEntities,
    ImportEntitiesVariables
  >(IMPORT_ENTITIES, {
    onCompleted: (data) => {
      if (data) {
        let type: 'INFO' | 'SUCCESS' = 'INFO';
        let message = "Aucune agence n'a été importée.";

        if (data?.importEntities > 0) {
          const importedEntitiesCount = data.importEntities;
          type = 'SUCCESS';
          message = `Les ${importedEntitiesCount} agences ont été importées avec succès.`;
        }

        displaySnackBar(client, {
          type,
          message,
          isOpen: true,
        });

        handleClose();
      }
    },
  });

  const handleImport = (inputs: ImportEntitiesInput[]) => {
    importEntities({ variables: { inputs } });
  };

  return (
    <CSVImportation<ICSVEntities>
      open={true}
      loading={loading}
      onImport={(data) => handleImport(reformatData(data))}
      headers={HEADERS}
      samples={samples}
      title="Importation d'agences"
      onClose={handleClose}
    />
  );
};

export default EntitiesCSVImportationModal;
